import React, { useState } from "react";
import axios from "axios";
import PageLayout from "../components/PageLayout";
import { SectionHeading } from "../components/PageHero";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useToast } from "../hooks/use-toast";
import { Plus, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const programs = [
  "Academic Core (3 Days)",
  "Enrichment & Formation (2 Days)",
  "Complete Program (5 Days)",
  "Not Sure Yet",
];

const gradeOptions = ["PK3", "PK4", "K", "1", "2", "3", "4", "5", "6", "7", "8"];

let childUid = 0;
const emptyChild = () => ({ uid: ++childUid, firstName: "", grade: "", program: "" });

const Inquire = () => {
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    parentFirstName: "",
    parentLastName: "",
    email: "",
    phone: "",
    children: [emptyChild()],
    seeking: "",
    hearAbout: "",
    updates: true,
  });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const updateChild = (idx, key, value) => {
    setForm((f) => {
      const children = [...f.children];
      children[idx] = { ...children[idx], [key]: value };
      return { ...f, children };
    });
  };

  const addChild = () =>
    setForm((f) => ({ ...f, children: [...f.children, emptyChild()] }));

  const removeChild = (idx) =>
    setForm((f) => ({ ...f, children: f.children.filter((_, i) => i !== idx) }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast({
        title: "Inquiry received",
        description: "Thank you for your interest in Angel of Peace Academy.",
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const message =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
          ? detail.map((d) => d.msg || "Invalid input").join(" · ")
          : "We could not submit your inquiry just now. Please try again in a moment.";
      toast({
        title: "Something went wrong",
        description: message,
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <PageLayout>
      {/* Header — hidden after successful submission */}
      {!submitted && (
        <section className="bg-[#fbf7ee] border-b border-[#eae1c4]">
          <div className="max-w-[1000px] mx-auto px-6 md:px-10 lg:px-14 py-16 md:py-20 text-center">
            <div className="font-sans-nav text-[12px] tracking-[0.32em] uppercase text-[#c9a961] mb-4">Inquire Today</div>
            <h1 className="font-display text-[#1e2952] text-[40px] md:text-[54px] leading-[1.06]" style={{ fontWeight: 500 }}>
              Become a <em className="not-italic text-[#c9a961]">Founding Family</em>.
            </h1>
            <div className="mt-6 flex items-center justify-center gap-3">
              <span className="block h-px w-40 bg-[#c9a961]"></span>
              <span className="text-[#c9a961]">†</span>
              <span className="block h-px w-40 bg-[#c9a961]"></span>
            </div>
            <p className="font-serif-body mt-7 text-[#3a466e] text-[17px] leading-[1.8] max-w-[720px] mx-auto">
              Tell us a little about your family and your interest in Angel of Peace Academy. We will follow up to answer questions and discuss whether the Academy may be a good fit for your family.
            </p>
          </div>
        </section>
      )}

      {/* Form or confirmation */}
      <section className="bg-[#fbf7ee]">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 py-16 md:py-20">
          {submitted ? (
            <div className="text-center bg-[#fbf7ee] border border-[#c9a961]/60 p-10 md:p-14" data-testid="inquiry-thank-you">
              <div className="flex items-center justify-center gap-3 mb-6">
                <span className="block h-px w-40 bg-[#c9a961]"></span>
                <span className="text-[#c9a961] text-lg">†</span>
                <span className="block h-px w-40 bg-[#c9a961]"></span>
              </div>
              <h2 className="font-display text-[#1e2952] text-[30px] md:text-[36px] mb-4" style={{ fontWeight: 600 }}>
                Thank You.
              </h2>
              <p className="font-serif-body text-[17px] leading-[1.8] text-[#3a466e]">
                Thank you for your interest in Angel of Peace Academy. We will be in touch and look forward to learning more about your family.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-10 bg-[#fbf7ee] border border-[#e6dfc8] p-8 md:p-12">
              {/* Parent Info */}
              <div>
                <div className="font-sans-nav text-[11.5px] tracking-[0.32em] uppercase text-[#c9a961] mb-5">Parent Information</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="pf" className="font-sans-nav text-[11px] tracking-[0.2em] uppercase text-[#1e2952]">First Name</Label>
                    <Input id="pf" required value={form.parentFirstName} onChange={update("parentFirstName")} className="mt-1 bg-white/60" />
                  </div>
                  <div>
                    <Label htmlFor="pl" className="font-sans-nav text-[11px] tracking-[0.2em] uppercase text-[#1e2952]">Last Name</Label>
                    <Input id="pl" required value={form.parentLastName} onChange={update("parentLastName")} className="mt-1 bg-white/60" />
                  </div>
                  <div>
                    <Label htmlFor="em" className="font-sans-nav text-[11px] tracking-[0.2em] uppercase text-[#1e2952]">Email</Label>
                    <Input id="em" type="email" required value={form.email} onChange={update("email")} className="mt-1 bg-white/60" />
                  </div>
                  <div>
                    <Label htmlFor="ph" className="font-sans-nav text-[11px] tracking-[0.2em] uppercase text-[#1e2952]">Phone</Label>
                    <Input id="ph" value={form.phone} onChange={update("phone")} className="mt-1 bg-white/60" />
                  </div>
                </div>
              </div>

              {/* Children */}
              <div>
                <div className="flex items-center justify-between mb-5">
                  <div className="font-sans-nav text-[11.5px] tracking-[0.32em] uppercase text-[#c9a961]">Children</div>
                  <button type="button" onClick={addChild} className="inline-flex items-center gap-2 font-sans-nav text-[11px] tracking-[0.24em] uppercase text-[#1e2952] hover:text-[#c9a961] transition-colors">
                    <Plus size={14} /> Add Child
                  </button>
                </div>

                <div className="space-y-6">
                  {form.children.map((child, idx) => (
                    <div key={child.uid} className="border border-[#e6dfc8] p-6 relative">
                      <div className="flex items-center justify-between mb-4">
                        <div className="font-display italic text-[#1e2952] text-[18px]">Child {idx + 1}</div>
                        {form.children.length > 1 && (
                          <button type="button" onClick={() => removeChild(idx)} className="text-[#8a7a4e] hover:text-[#1e2952] transition-colors">
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label className="font-sans-nav text-[11px] tracking-[0.2em] uppercase text-[#1e2952]">First Name</Label>
                          <Input value={child.firstName} onChange={(e) => updateChild(idx, "firstName", e.target.value)} className="mt-1 bg-white/60" />
                        </div>
                        <div>
                          <Label className="font-sans-nav text-[11px] tracking-[0.2em] uppercase text-[#1e2952]">Current Grade</Label>
                          <Select value={child.grade} onValueChange={(v) => updateChild(idx, "grade", v)}>
                            <SelectTrigger
                              data-testid={`child-${idx}-grade-select`}
                              className="mt-1 bg-white/60 font-serif-body text-[15px] text-[#1e2952]"
                            >
                              <SelectValue placeholder="Select grade" />
                            </SelectTrigger>
                            <SelectContent>
                              {gradeOptions.map((g) => (
                                <SelectItem key={g} value={g}>
                                  {g}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="mt-5">
                        <Label className="font-sans-nav text-[11px] tracking-[0.2em] uppercase text-[#1e2952] mb-3 block">Program Interest</Label>
                        <RadioGroup value={child.program} onValueChange={(v) => updateChild(idx, "program", v)} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {programs.map((p) => (
                            <label key={p} className="flex items-center gap-3 font-serif-body text-[15px] text-[#1e2952] cursor-pointer">
                              <RadioGroupItem value={p} id={`p-${idx}-${p}`} />
                              <span>{p}</span>
                            </label>
                          ))}
                        </RadioGroup>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Seeking */}
              <div>
                <Label htmlFor="seeking" className="font-sans-nav text-[11px] tracking-[0.2em] uppercase text-[#1e2952]">What are you seeking in a school for your children?</Label>
                <Textarea id="seeking" rows={4} value={form.seeking} onChange={update("seeking")} className="mt-1 bg-white/60" />
              </div>

              <div>
                <Label htmlFor="hear" className="font-sans-nav text-[11px] tracking-[0.2em] uppercase text-[#1e2952]">How did you hear about Angel of Peace Academy? <span className="text-[#8a7a4e] normal-case tracking-normal">(optional)</span></Label>
                <Input id="hear" value={form.hearAbout} onChange={update("hearAbout")} className="mt-1 bg-white/60" />
              </div>

              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox checked={form.updates} onCheckedChange={(v) => setForm((f) => ({ ...f, updates: !!v }))} className="mt-1" />
                <span className="font-serif-body text-[15px] text-[#1e2952] leading-[1.6]">
                  I would like to receive updates about Angel of Peace Academy.
                </span>
              </label>

              <div className="pt-4">
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="inquiry-submit-button"
                  className="btn-navy font-sans-nav text-[12px] tracking-[0.28em] uppercase px-10 py-4 w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {sending ? "Sending…" : "Send Inquiry"}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </PageLayout>
  );
};

export default Inquire;
